import { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../services/api';
import type { Work } from '../types/api';
import { PageIntro, Pagination, ProjectCard, ScrollTabs } from '../components/ui';
import { capitalize, formatLocation } from '../utils/text';

const PAGE_SIZE = 10;
const ALL = 'all';
const when = (w: Work) => (w.completed_date ? Date.parse(w.completed_date) : 0) || 0;

export default function Portfolio() {
  const [works, setWorks] = useState<Work[]>([]);
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [filter, setFilter] = useState(ALL);
  const [page, setPage] = useState(1);
  const gridTop = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    apiService.getWorks({ enable: true })
      .then((w) => { if (alive) { setWorks([...w].sort((a, b) => when(b) - when(a))); setStatus('ok'); } })
      .catch(() => alive && setStatus('error'));
    return () => { alive = false; };
  }, []);

  // Un onglet par service qui a au moins une réalisation, les plus fournis d'abord
  const tabs = useMemo(() => {
    const byService = new Map<string, { label: string; n: number }>();
    for (const w of works) {
      const id = w.service_id;
      const name = w.service?.name?.trim();
      if (!id || !name) continue;
      byService.set(id, { label: name, n: (byService.get(id)?.n ?? 0) + 1 });
    }
    return [{ id: ALL, label: 'Tous' }, ...[...byService].sort((a, b) => b[1].n - a[1].n).map(([id, v]) => ({ id, label: v.label }))];
  }, [works]);

  const filtered = useMemo(() => (filter === ALL ? works : works.filter((w) => w.service_id === filter)), [works, filter]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const pick = (id: string) => { setFilter(id); setPage(1); };
  const goPage = (n: number) => { setPage(n); gridTop.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  return (
    <div id="content">
      <PageIntro eyebrow="Nos réalisations" title="Bâtir l'avenir de la construction, un projet à la fois." />

      <section className="bg-white pb-20 text-black md:pb-28">
        <div className="site-container">
          {tabs.length > 2 && <ScrollTabs tabs={tabs} active={filter} onChange={pick} className="mb-10 md:mb-14" />}

          <div ref={gridTop} className="scroll-mt-28" />
          {status === 'loading' && (
            <div className="grid gap-x-6 gap-y-16 md:grid-cols-2" aria-busy>
              {[0, 1, 2, 3].map((n) => <div key={n} className="aspect-video animate-pulse bg-secondary-200" />)}
            </div>
          )}
          {status === 'error' && <p className="py-10 text-lg text-ink">Impossible de charger les réalisations pour le moment. Réessayez dans un instant.</p>}
          {status === 'ok' && visible.length === 0 && <p className="py-10 text-lg text-ink">Aucune réalisation dans cette catégorie pour le moment.</p>}

          {visible.length > 0 && (
            <div key={`${filter}-${current}`} className="grid gap-x-6 gap-y-12 md:grid-cols-2 md:gap-y-20">
              {visible.map((w, n) => (
                <ProjectCard
                  key={w.id}
                  to={`/works/${w.id}`}
                  image={w.cover_image?.url}
                  title={capitalize(w.title)}
                  location={formatLocation(w.location)}
                  className="animate-reveal"
                  style={{ animationDelay: `${(n % 2) * 0.06 + Math.floor(n / 2) * 0.08}s` }}
                />
              ))}
            </div>
          )}

          {status === 'ok' && <Pagination page={current} pages={pages} total={filtered.length} pageSize={PAGE_SIZE} onChange={goPage} />}
        </div>
      </section>
    </div>
  );
}
